<template>
  <b-modal 
    size="lg" 
    ref="modal" 
    @hidden="flush" 
    centered 
    no-close-on-backdrop
    hide-footer
    body-class="p-0"
  >
    <template #modal-header="{ close }">
      <div class="modal-header-custom">
        <h5 class="modal-title">
          Add New Financing Candidate
        </h5>
        <button type="button" class="close-btn" @click="close()" aria-label="Close">
          <i class="las la-times"></i>
        </button>
      </div>
    </template>

    <div class="form-modal-body">
      <validation-observer ref="observer">
        <div
          v-if="error && error.length"
          class="error-alert"
        >
          <i class="las la-exclamation-circle"></i>
          <span v-html="error"></span>
        </div>
        
        <div class="form-content">
          <div class="form-section">
            <validation-provider
              name="name"
              rules="required|max:100"
              v-slot="{ errors }"
            >
              <label class="input-label">
                Company Name *
              </label>
              <input
                type="text"
                :disabled="disabled"
                v-model="candidate.name"
                class="form-input"
                placeholder="Enter company name"
              />
              <div v-if="errors && errors.length" class="input-error">
                <span v-for="(error, key) in errors" :key="key">{{ error }}</span>
              </div>
            </validation-provider>
          </div>

          <!-- Emirates ID Upload -->
          <div class="form-section">
            <label class="input-label">
              Emirates ID *
            </label>
            <div v-if="!emiratesIdFile" class="file-input-container-small" :class="{ disabled: disabled }">
              <input 
                type="file" 
                accept="image/*,.pdf" 
                @change="handleEmiratesIdChange" 
                ref="emiratesIdInput"
                class="file-input"
                :disabled="disabled"
              />
              <div class="file-input-overlay-small" @click="triggerEmiratesIdInput">
                <i class="las la-id-card upload-icon-small"></i>
                <span class="file-hint-small">Image or PDF</span>
              </div>
            </div>
            <div v-else class="selected-file-small">
              <div class="file-info-small">
                <i :class="emiratesIdFile.type === 'application/pdf' ? 'las la-file-pdf' : 'las la-file-image'"></i>
                <span class="file-name-small">{{ emiratesIdFile.name }}</span>
              </div>
              <button class="change-file-btn-small" @click="changeEmiratesId" v-if="!disabled && !sending" type="button">
                <i class="las la-times"></i>
              </button>
            </div>
          </div>

          <!-- Trade License Upload -->
          <div class="form-section">
            <label class="input-label">
              Trade License *
            </label>
            <div v-if="!tradeLicenseFile" class="file-input-container-small" :class="{ disabled: disabled }">
              <input 
                type="file" 
                accept="application/pdf" 
                @change="handleTradeLicenseChange" 
                ref="tradeLicenseInput"
                class="file-input"
                :disabled="disabled"
              />
              <div class="file-input-overlay-small" @click="triggerTradeLicenseInput">
                <i class="las la-file-pdf upload-icon-small"></i>
                <span class="file-hint-small">PDF</span>
              </div>
            </div>
            <div v-else class="selected-file-small">
              <div class="file-info-small">
                <i class="las la-file-pdf"></i>
                <span class="file-name-small">{{ tradeLicenseFile.name }}</span>
              </div>
              <button class="change-file-btn-small" @click="changeTradeLicense" v-if="!disabled && !sending" type="button">
                <i class="las la-times"></i>
              </button>
            </div>
          </div>
        </div>
      </validation-observer>

      <!-- Progress Section -->
      <div v-if="processingProgress.show" class="progress-section">
        <ScanProgressBar 
          :show-progress="processingProgress.show"
          :progress-percentage="processingProgress.percentage"
          :current-page="processingProgress.currentPage"
          :total-pages="processingProgress.totalPages"
          :task-type="processingProgress.taskType"
          :progress-message="processingProgress.message"
        />
      </div>

      <div class="modal-actions">
        <button 
          class="btn-submit" 
          @click.stop="submit"
          :disabled="disabled || sending"
        >
          <span v-if="sending">
            <i class="las la-spinner la-spin"></i>
            Submitting...
          </span>
          <span v-else>
            <i class="las la-check"></i>
            Submit
          </span>
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import ChatbotService from "@/services/chatbot.service";
import { formatErrorMessage } from "@/utils/random_utils";
import ScanProgressBar from "./ScanProgressBar.vue";

export default {
  name: "FinancingCandidateForm",
  components: {
    ValidationObserver,
    ValidationProvider,
    ScanProgressBar,
  },
  data() {
    return {
      error: null,
      sending: false,
      disabled: false,
      candidate: {
        name: null,
        message: null,
      },
      emiratesIdFile: null,
      tradeLicenseFile: null,
      websocket: null,
      currentCandidateId: null,
      processingProgress: {
        show: false,
        percentage: 0,
        message: '',
        currentPage: 0,
        totalPages: 0,
        taskType: 'file-processing',
        uploadIds: [],
        currentFileIndex: 0,
        currentFileProgress: 0
      },
    };
  },
  computed: {
    variants() {
      return this.error ? "alert-danger" : "";
    },
  },
  methods: {
    async submit() {
      try {
        const isValid = await this.$refs.observer.validate();
        if (!isValid) return;

        // Validate files
        if (!this.emiratesIdFile) {
          this.error = 'Please upload Emirates ID file';
          return;
        }
        if (!this.tradeLicenseFile) {
          this.error = 'Please upload Trade License file';
          return;
        }

        this.sending = true;
        this.error = null;

        // Create candidate first
        const response = await ChatbotService.setFinancingCandidate(this.candidate);
        const candidateId = response.id || response.candidate_id;
        
        if (!candidateId) {
          throw new Error('Failed to get candidate ID from response');
        }

        // Upload files sequentially
        try {
          this.currentCandidateId = candidateId;
          
          // Show progress
          this.processingProgress.show = true;
          this.processingProgress.percentage = 0;
          this.processingProgress.message = 'Uploading files...';
          this.processingProgress.taskType = 'file-processing';
          
          // Upload Emirates ID first
          this.processingProgress.message = 'Uploading Emirates ID...';
          this.processingProgress.currentFileIndex = 0;
          this.processingProgress.currentFileProgress = 0;
          const emiratesIdResponse = await ChatbotService.uploadFile(candidateId, this.emiratesIdFile, 'emirates_id');
          
          // Process Emirates ID upload if it needs processing
          if (emiratesIdResponse && emiratesIdResponse.upload_id) {
            const needsProcessing = emiratesIdResponse.status === 'processing' || emiratesIdResponse.status === 'extracting';
            
            if (needsProcessing) {
              await this.processUploadWithWebSocket(emiratesIdResponse.upload_id, 'Emirates ID', 0);
            } else {
              // File completed immediately, mark as 50% done
              this.processingProgress.percentage = 50;
            }
          }
          
          // Upload Trade License second
          this.processingProgress.message = 'Uploading Trade License...';
          this.processingProgress.currentFileIndex = 1;
          this.processingProgress.currentFileProgress = 0;
          this.processingProgress.percentage = 50; // Start at 50% (first file done)
          const tradeLicenseResponse = await ChatbotService.uploadFile(candidateId, this.tradeLicenseFile, 'trade_license');
          
          // Process Trade License upload if it needs processing
          if (tradeLicenseResponse && tradeLicenseResponse.upload_id) {
            const needsProcessing = tradeLicenseResponse.status === 'processing' || tradeLicenseResponse.status === 'extracting';
            
            if (needsProcessing) {
              await this.processUploadWithWebSocket(tradeLicenseResponse.upload_id, 'Trade License', 1);
            } else {
              // File completed immediately, mark as 100% done
              this.processingProgress.percentage = 100;
            }
          }
          
          // All uploads completed
          this.processingProgress.show = false;
          this.processingProgress.percentage = 100;
          
          this.$bvToast.toast('Files uploaded and processed successfully.', {
            title: 'Success',
            variant: 'success',
            solid: true,
            autoHideDelay: 5000
          });
          
          this.$emit('candidate-added', response);

          this.$refs.modal.hide();
          this.flush();
          this.$router.push({
            name: 'chatbot-automated-assessment-input',
            params: { candidateId: String(candidateId) }
          });
          
        } catch (uploadError) {
          console.error('Error uploading files:', uploadError);
          this.error = formatErrorMessage(uploadError) || 'Failed to upload files. Candidate was created but files could not be uploaded.';
          this.processingProgress.show = false;
          this.sending = false;
          this.disconnectWebSocket();
          return;
        }
        
      } catch (error) {
        this.error = formatErrorMessage(error);
      } finally {
        this.sending = false;
      }
    },

    flush() {
      this.candidate = {
        name: null,
        message: null,
      };
      this.emiratesIdFile = null;
      this.tradeLicenseFile = null;
      this.error = null;
      this.sending = false;
      this.disabled = false;
      this.currentCandidateId = null;
      this.processingProgress = {
        show: false,
        percentage: 0,
        message: '',
        currentPage: 0,
        totalPages: 0,
        taskType: 'file-processing',
        uploadIds: []
      };
      
      // Disconnect WebSocket
      this.disconnectWebSocket();
      
      // Reset file inputs
      if (this.$refs.emiratesIdInput) {
        this.$refs.emiratesIdInput.value = '';
      }
      if (this.$refs.tradeLicenseInput) {
        this.$refs.tradeLicenseInput.value = '';
      }
      
      if (this.$refs.observer) {
        this.$refs.observer.reset();
      }
    },

    async processUploadWithWebSocket(uploadId, fileName, fileIndex) {
      return new Promise((resolve, reject) => {
        if (!uploadId) {
          resolve();
          return;
        }
        
        this.processingProgress.message = `Processing ${fileName}...`;
        
        try {
          const wsUrl = `${process.env.VUE_APP_SOCKET_URL}/uploads/${uploadId}/`;
          
          console.log('Connecting to File Processing WebSocket:', wsUrl);
          
          const websocket = new WebSocket(wsUrl);
          
          websocket.onopen = () => {
            console.log(`File Processing WebSocket connected for ${fileName} (${uploadId})`);
          };
          
          websocket.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data);
              console.log('WebSocket message:', data);
              
              // Handle progress updates - calculate based on current/total pages
              if (data.status === 'processing' || data.status === 'extracting') {
                const currentPage = data.current_page || 0;
                const totalPages = data.total_pages || 1;
                
                // Calculate progress within this file (0-100%)
                const fileProgress = totalPages > 0 ? Math.min((currentPage / totalPages) * 100, 100) : 0;
                this.processingProgress.currentFileProgress = fileProgress;
                
                // Calculate overall progress: fileIndex * 50% + (fileProgress * 50%)
                const baseProgress = fileIndex * 50; // 0% for first file, 50% for second file
                const fileContribution = (fileProgress / 100) * 50; // This file contributes up to 50%
                this.processingProgress.percentage = Math.min(baseProgress + fileContribution, fileIndex === 0 ? 50 : 100);
                
                this.processingProgress.currentPage = currentPage;
                this.processingProgress.totalPages = totalPages;
                
                // Update status based on data
                if (data.status === 'extracting') {
                  this.processingProgress.message = `Extracting data from ${fileName}...`;
                } else if (data.status === 'processing') {
                  this.processingProgress.message = `Processing ${fileName}...`;
                }
              }
              
              // Only update progress to completion when file finishes
              if (data.status === 'completed') {
                console.log(`${fileName} processing completed`);
                // Set to completion for this file's portion
                const baseProgress = fileIndex * 50;
                this.processingProgress.percentage = baseProgress + 50; // Complete this file's 50%
                this.processingProgress.currentFileProgress = 100;
                websocket.close();
                resolve();
              }
              
              // Handle errors
              if (data.status === 'failed') {
                const errorMessage = data.error || data.message || `Failed to process ${fileName}.`;
                console.error(`Error processing ${fileName}:`, errorMessage);
                websocket.close();
                reject(new Error(errorMessage));
              }
            } catch (error) {
              console.error('Error parsing WebSocket message:', error);
            }
          };
          
          websocket.onerror = (error) => {
            console.error(`File Processing WebSocket error for ${fileName}:`, error);
            websocket.close();
            reject(new Error(`WebSocket error for ${fileName}`));
          };
          
          websocket.onclose = () => {
            console.log(`File Processing WebSocket disconnected for ${fileName}`);
          };
          
          // Set a timeout to prevent hanging indefinitely
          setTimeout(() => {
            if (websocket.readyState === WebSocket.OPEN || websocket.readyState === WebSocket.CONNECTING) {
              console.warn(`WebSocket timeout for ${fileName}, closing connection`);
              websocket.close();
              resolve(); // Resolve instead of reject to continue with next upload
            }
          }, 300000); // 5 minute timeout
          
        } catch (error) {
          console.error(`Error connecting to File Processing WebSocket for ${fileName}:`, error);
          reject(error);
        }
      });
    },
    
    disconnectWebSocket() {
      if (this.websocket) {
        this.websocket.close();
        this.websocket = null;
      }
    },

    handleEmiratesIdChange(event) {
      const file = event.target.files[0];
      if (file) {
        const isImage = file.type.startsWith('image/');
        const isPdf = file.type === 'application/pdf';
        if (isImage || isPdf) {
          this.emiratesIdFile = file;
          this.error = null;
        } else {
          this.error = 'Please select an image file (JPG, PNG) or PDF file for Emirates ID.';
          if (this.$refs.emiratesIdInput) {
            this.$refs.emiratesIdInput.value = '';
          }
        }
      }
    },

    handleTradeLicenseChange(event) {
      const file = event.target.files[0];
      if (file && file.type === 'application/pdf') {
        this.tradeLicenseFile = file;
        this.error = null;
      } else if (file) {
        this.error = 'Please select a PDF file for Trade License.';
        if (this.$refs.tradeLicenseInput) {
          this.$refs.tradeLicenseInput.value = '';
        }
      }
    },

    changeEmiratesId() {
      this.emiratesIdFile = null;
      if (this.$refs.emiratesIdInput) {
        this.$refs.emiratesIdInput.value = '';
        this.$refs.emiratesIdInput.click();
      }
    },

    changeTradeLicense() {
      this.tradeLicenseFile = null;
      if (this.$refs.tradeLicenseInput) {
        this.$refs.tradeLicenseInput.value = '';
        this.$refs.tradeLicenseInput.click();
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },

    triggerEmiratesIdInput() {
      if (!this.disabled && this.$refs.emiratesIdInput) {
        this.$refs.emiratesIdInput.click();
      }
    },

    triggerTradeLicenseInput() {
      if (!this.disabled && this.$refs.tradeLicenseInput) {
        this.$refs.tradeLicenseInput.click();
      }
    },

    open() {
      this.$refs.modal.show();
    },
  },
  beforeDestroy() {
    // Clean up WebSocket connection
    this.disconnectWebSocket();
  },
};
</script>

<style lang="scss" scoped>
@import '../index.scss';

.modal-header-custom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0;
  
  .modal-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1.25rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
    font-family: $brand-font-medium;
    
    i {
      color: $color-primary;
      font-size: 1.5rem;
    }
  }
  
  .close-btn {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #6c757d;
    cursor: pointer;
    padding: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s ease;
    
    &:hover {
      background: #f3f4f6;
      color: #2d3748;
    }
  }
}

.form-modal-body {
  padding: 1.5rem;
}

.error-alert {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  font-family: $brand-font-regular;
  
  i {
    font-size: 1.1rem;
    flex-shrink: 0;
  }
}

.form-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.input-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
  font-family: $brand-font-medium;
  
  i {
    color: $color-primary;
    font-size: 1.25rem;
  }
}

.form-input {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  font-family: $brand-font-regular;
  color: #2d3748;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: $color-primary;
    box-shadow: 0 0 0 3px rgba($color-primary, 0.1);
  }
  
  &:disabled {
    background: #f7fafc;
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
}

.input-error {
  color: #dc2626;
  font-size: 0.875rem;
  font-family: $brand-font-regular;
  margin-top: 0.25rem;
}

.emirates-id-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.file-upload-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.file-upload-label-small {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4a5568;
  font-family: $brand-font-medium;
}

.file-input-container-small {
  position: relative;
  width: 100%;
  height: 60px;
  min-height: 60px;
  border: 2px dashed #e2e8f0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  cursor: pointer;
  background: #f8f9fa;
  
  &:hover {
    border-color: $color-primary;
    background: rgba($color-primary, 0.05);
  }
  
  &.disabled {
    cursor: not-allowed;
    opacity: 0.6;
    
    &:hover {
      border-color: #e2e8f0;
      background: #f8f9fa;
    }
  }
  
  .file-input {
    position: absolute;
    opacity: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
    
    &:disabled {
      cursor: not-allowed;
    }
  }
  
  .file-input-overlay-small {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    pointer-events: none;
    color: #4a5568;
    
    .upload-icon-small {
      font-size: 1.25rem;
      color: $color-primary;
      margin-bottom: 0.2rem;
    }
    
    .file-hint-small {
      font-size: 0.7rem;
      color: #718096;
      font-family: $brand-font-regular;
    }
  }
}

.selected-file-small {
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  padding: 0.75rem;
  min-height: 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  
  .file-info-small {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
    min-width: 0;
    
    i {
      color: #16a34a;
      font-size: 1.25rem;
      flex-shrink: 0;
    }
    
    .file-name-small {
      color: #166534;
      font-weight: 500;
      font-size: 0.875rem;
      font-family: $brand-font-medium;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  
  .change-file-btn-small {
    background: rgba(#dc2626, 0.1);
    color: #dc2626;
    border: none;
    width: 24px;
    height: 24px;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
    
    &:hover {
      background: rgba(#dc2626, 0.2);
    }
    
    i {
      font-size: 0.875rem;
    }
  }
}

.progress-section {
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  
  // Remove nested box styling from ScanProgressBar
  :deep(.scan-progress-container) {
    background: transparent;
    border: none;
    padding: 0;
    box-shadow: none;
  }
  
  :deep(.scan-progress-header) {
    margin-bottom: 0.75rem;
  }
  
  :deep(.progress-bar-container) {
    margin-bottom: 0.5rem;
  }
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding-top: 1.5rem;
  margin-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.btn-submit {
  background: $color-primary;
  color: $color-secondary;
  border: none;
  padding: 0.875rem 1.75rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  font-family: $brand-font-medium;
  
  &:hover:not(:disabled) {
    background: darken($color-primary, 5%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba($color-primary, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  i {
    font-size: 1.1rem;
  }
}

@media (max-width: 768px) {
  .emirates-id-row {
    grid-template-columns: 1fr;
  }
  
  .form-modal-body {
    padding: 1rem;
  }
}
</style> 